/**
 * Generic question-graph interview (PRP-CALC-1 S8 amendment).
 *
 * Renders whatever graph the backend serves — no per-use-case form logic.
 * Adding a crane use case is a content change in data/scenarios/crane.json;
 * this component never changes for it.
 */
import { useEffect, useMemo, useState } from 'react';

import { Button } from '../../components/ui/Button';
import CalcResults from './CalcResults';
import {
  ScenarioAPI,
  factKey,
  isVisible,
  type Answers,
  type AnswerValue,
  type DerivedValue,
  type QuestionGraph,
  type QuestionNode,
  type ScenarioRunResponse,
} from './scenarioApi';

function PlanViewDiagram() {
  // Plan view: crane -> building face -> farthest unit (S8.1 requirement).
  return (
    <svg
      viewBox="0 0 320 96"
      role="img"
      aria-label="Plan view: crane setup distance to the building face, plus the unit's distance from the roof edge, make up the pick radius"
      className="mt-1 w-full max-w-sm text-muted-foreground"
    >
      <rect x="120" y="12" width="180" height="72" fill="none" stroke="currentColor" />
      <text x="210" y="52" textAnchor="middle" fontSize="10" fill="currentColor">
        roof (plan view)
      </text>
      <circle cx="24" cy="48" r="8" fill="none" stroke="currentColor" />
      <text x="24" y="76" textAnchor="middle" fontSize="9" fill="currentColor">
        crane
      </text>
      <rect x="248" y="40" width="16" height="16" fill="none" stroke="currentColor" />
      <text x="256" y="34" textAnchor="middle" fontSize="9" fill="currentColor">
        RTU
      </text>
      <line x1="32" y1="48" x2="120" y2="48" stroke="currentColor" strokeDasharray="4 3" />
      <text x="76" y="42" textAnchor="middle" fontSize="9" fill="currentColor">
        setup dist
      </text>
      <line x1="120" y1="64" x2="248" y2="64" stroke="currentColor" strokeDasharray="4 3" />
      <text x="184" y="78" textAnchor="middle" fontSize="9" fill="currentColor">
        distance from edge
      </text>
    </svg>
  );
}

function QuestionField({
  node,
  value,
  highlight,
  onChange,
}: {
  node: QuestionNode;
  value: AnswerValue | undefined;
  highlight: boolean;
  onChange: (value: AnswerValue | undefined) => void;
}) {
  const inputId = `q-${node.id}`;
  const label = (
    <label htmlFor={inputId} className="text-sm font-medium">
      {node.text}
      {node.required && <span className="text-destructive"> *</span>}
    </label>
  );
  const hint = node.helper_text && (
    <p className="text-xs text-muted-foreground">{node.helper_text}</p>
  );
  const frame = highlight
    ? 'rounded-md border border-destructive/60 bg-destructive/5 p-2'
    : '';

  if (node.answer_type === 'choice') {
    return (
      <div className={`space-y-1 ${frame}`}>
        {label}
        <select
          id={inputId}
          className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
          value={value === undefined ? '' : String(value)}
          onChange={(e) => onChange(e.target.value || undefined)}
        >
          <option value="">Select…</option>
          {node.options.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
              {o.stub ? ' (coming soon)' : ''}
            </option>
          ))}
        </select>
        {hint}
      </div>
    );
  }
  if (node.answer_type === 'multichoice') {
    const selected = Array.isArray(value) ? value : [];
    return (
      <fieldset className={`space-y-1 ${frame}`}>
        <legend className="text-sm font-medium">
          {node.text}
          {node.required && <span className="text-destructive"> *</span>}
        </legend>
        <div className="flex flex-wrap gap-3">
          {node.options.map((o) => (
            <label key={o.value} className="flex items-center gap-1.5 text-sm">
              <input
                type="checkbox"
                checked={selected.includes(o.value)}
                onChange={(e) =>
                  onChange(
                    e.target.checked
                      ? [...selected, o.value]
                      : selected.filter((v) => v !== o.value),
                  )
                }
              />
              {o.label}
            </label>
          ))}
        </div>
        {hint}
      </fieldset>
    );
  }
  if (node.answer_type === 'boolean') {
    return (
      <div className={frame}>
        <label className="flex items-center gap-2 text-sm font-medium">
          <input
            type="checkbox"
            checked={value === true}
            onChange={(e) => onChange(e.target.checked)}
          />
          {node.text}
        </label>
        {hint}
      </div>
    );
  }
  // number / text (dimensions & lift_list render as text in v1)
  return (
    <div className={`space-y-1 ${frame}`}>
      {label}
      <div className="flex items-center gap-2">
        <input
          id={inputId}
          type={node.answer_type === 'number' ? 'number' : 'text'}
          className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
          value={value === undefined ? '' : String(value)}
          onChange={(e) => {
            if (e.target.value === '') return onChange(undefined);
            onChange(
              node.answer_type === 'number' ? Number(e.target.value) : e.target.value,
            );
          }}
        />
        {node.unit && (
          <span className="text-sm text-muted-foreground">{node.unit}</span>
        )}
      </div>
      {node.diagram === 'plan-view-radius' && <PlanViewDiagram />}
      {hint}
    </div>
  );
}

function DerivedPanel({
  derived,
  overrides,
  onOverride,
}: {
  derived: DerivedValue[];
  overrides: Record<string, number>;
  onOverride: (key: string, value: number) => void;
}) {
  return (
    <div className="rounded-lg border border-border p-4">
      <h3 className="text-sm font-semibold">Derived engineering inputs</h3>
      <p className="mt-1 text-xs text-muted-foreground">
        Every number below is derived from your answers with the math shown.
        Edit any value and re-run — edits feed the calculator directly.
      </p>
      <div className="mt-3 space-y-3">
        {derived.map((d) => (
          <div key={d.key} className="text-sm">
            <div className="flex items-center gap-2">
              <span className="font-medium">{d.label}:</span>
              {d.editable ? (
                <input
                  type="number"
                  aria-label={d.label}
                  className="w-28 rounded-md border border-input bg-background px-2 py-1 text-sm"
                  value={overrides[d.key] ?? d.value}
                  onChange={(e) => onOverride(d.key, Number(e.target.value))}
                />
              ) : (
                <span>{d.value}</span>
              )}
              <span className="text-muted-foreground">{d.unit}</span>
            </div>
            <p className="text-xs text-muted-foreground">= {d.formula}</p>
            {d.flag && (
              <p className="text-xs font-medium text-amber-600 dark:text-amber-400">
                {d.flag}
              </p>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// B-666 review fix: per-calculator copy — the run button, free-text
// placeholder, and stub note previously hardcoded crane wording even
// though this component renders generically for scaffold/lift too.
const RUN_LABELS: Record<string, string> = {
  crane: 'Size the crane from these answers',
  scaffold: 'Estimate the scaffold from these answers',
  lift: 'Recommend a lift from these answers',
};

const FREE_TEXT_PLACEHOLDERS: Record<string, string> = {
  crane:
    'e.g. Two-story office, 120 by 320, 40 ft parapet. Crane can only get within 20 ft of the front. Roof replacement with 4 RTUs, farthest about 60 ft from the front edge. Building stays occupied, roofing takes 3 weeks.',
  scaffold:
    'e.g. Stucco repair on the front and rear elevations, 40 ft and 24 ft, 20 ft tall. Frame scaffold, up for about 4 weeks, no debris netting needed.',
  lift:
    'e.g. Soffit and fascia repair on a two-story house, work is about 20 ft off the ground, no obstruction to reach over. Outdoor, firm ground, need it for about a week.',
};

function runLabel(calculator: string): string {
  return RUN_LABELS[calculator] || 'Run the numbers from these answers';
}

function freeTextPlaceholder(calculator: string): string {
  return (
    FREE_TEXT_PLACEHOLDERS[calculator] ||
    'Describe the job in your own words — the tool fills what it can and asks for the rest.'
  );
}

export default function ScenarioInterview({ calculator }: { calculator: string }) {
  const [graph, setGraph] = useState<QuestionGraph | null>(null);
  const [graphError, setGraphError] = useState<string | null>(null);
  const [answers, setAnswers] = useState<Answers>({});
  const [overrides, setOverrides] = useState<Record<string, number>>({});
  const [freeText, setFreeText] = useState('');
  const [parsing, setParsing] = useState(false);
  const [parseNote, setParseNote] = useState<string | null>(null);
  const [running, setRunning] = useState(false);
  const [runError, setRunError] = useState<string | null>(null);
  const [result, setResult] = useState<ScenarioRunResponse | null>(null);

  useEffect(() => {
    ScenarioAPI.graph(calculator)
      .then(setGraph)
      .catch((e) =>
        setGraphError(e instanceof Error ? e.message : 'Could not load questions.'),
      );
  }, [calculator]);

  const visibleNodes = useMemo(
    () => (graph ? graph.nodes.filter((n) => isVisible(n, answers)) : []),
    [graph, answers],
  );
  const stubSelected = useMemo(() => {
    if (!graph) return null;
    for (const node of graph.nodes) {
      const v = answers[factKey(node)];
      if (v === undefined) continue;
      const values = Array.isArray(v) ? v.map(String) : [String(v)];
      const stub = node.options.find((o) => o.stub && values.includes(o.value));
      if (stub) return stub;
    }
    return null;
  }, [graph, answers]);

  function setAnswer(key: string, value: AnswerValue | undefined) {
    setAnswers((prev) => {
      const next = { ...prev };
      if (value === undefined) delete next[key];
      else next[key] = value;
      return next;
    });
  }

  async function onParse() {
    setParsing(true);
    setParseNote(null);
    try {
      const { answers: parsed } = await ScenarioAPI.parse(calculator, freeText);
      setAnswers((prev) => ({ ...prev, ...parsed }));
      const n = Object.keys(parsed).length;
      setParseNote(
        n
          ? `Filled ${n} answer${n === 1 ? '' : 's'} from your description — anything still highlighted needs your input. Nothing was guessed.`
          : 'Nothing extractable found — answer the questions below.',
      );
    } catch (e) {
      setParseNote(
        e instanceof Error ? e.message : 'Parse unavailable — use the guided questions.',
      );
    } finally {
      setParsing(false);
    }
  }

  async function onRun(nextOverrides = overrides) {
    setRunning(true);
    setRunError(null);
    try {
      setResult(await ScenarioAPI.run(calculator, answers, nextOverrides));
    } catch (e) {
      setRunError(e instanceof Error ? e.message : 'Something went wrong.');
    } finally {
      setRunning(false);
    }
  }

  if (graphError) {
    return (
      <p role="alert" className="text-sm text-destructive">
        {graphError} — use Advanced mode below.
      </p>
    );
  }
  if (!graph) {
    return <p className="text-sm text-muted-foreground">Loading questions…</p>;
  }

  const missing = new Set(result?.status === 'questions' ? result.missing : []);
  if (result?.status === 'cannot-derive') result.missing.forEach((m) => missing.add(m));

  return (
    <div className="space-y-6">
      {/* Free-text intake (S8.4) */}
      <div className="space-y-2">
        <label htmlFor="job-description" className="text-sm font-medium">
          Describe the job
        </label>
        <textarea
          id="job-description"
          rows={4}
          className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
          placeholder={freeTextPlaceholder(calculator)}
          value={freeText}
          onChange={(e) => setFreeText(e.target.value)}
        />
        <Button
          type="button"
          variant="secondary"
          isLoading={parsing}
          disabled={!freeText.trim()}
          onClick={onParse}
        >
          Fill answers from description
        </Button>
        {parseNote && <p className="text-xs text-muted-foreground">{parseNote}</p>}
      </div>

      {/* Graph-driven interview */}
      <div className="space-y-4" aria-label="Job questions">
        {visibleNodes.map((node) => (
          <QuestionField
            key={node.id}
            node={node}
            value={answers[factKey(node)]}
            highlight={missing.has(node.id)}
            onChange={(v) => setAnswer(factKey(node), v)}
          />
        ))}
      </div>

      {stubSelected ? (
        <div className="rounded-md border border-border bg-muted/40 p-3 text-sm">
          <span className="font-medium">{stubSelected.label}</span> is coming
          soon — that use case isn't built yet. The Advanced mode form below
          works for any {calculator} job today.
        </div>
      ) : (
        <Button type="button" isLoading={running} onClick={() => onRun()} className="w-full">
          {runLabel(calculator)}
        </Button>
      )}
      {runError && (
        <p role="alert" className="text-sm text-destructive">
          {runError}
        </p>
      )}
      {result?.message && result.status !== 'ok' && (
        <p className="text-sm text-muted-foreground">{result.message}</p>
      )}

      {/* Derived panel + results (S8.2 transparency, S8.3 narrative-first) */}
      {result?.status === 'ok' && (
        <div className="space-y-4">
          <DerivedPanel
            derived={result.derived}
            overrides={overrides}
            onOverride={(key, value) => {
              const next = { ...overrides, [key]: value };
              setOverrides(next);
            }}
          />
          {Object.keys(overrides).length > 0 && (
            <Button
              type="button"
              variant="secondary"
              isLoading={running}
              onClick={() => onRun()}
            >
              Re-run with edited values
            </Button>
          )}
          {result.scenario_narrative && (
            <div className="rounded-lg border border-border bg-muted/30 p-4 text-sm">
              {result.scenario_narrative}
            </div>
          )}
          {result.alternates.length > 0 && (
            <div className="rounded-lg border border-border p-4">
              <h3 className="text-sm font-semibold">Alternates &amp; flags</h3>
              <ul className="mt-2 list-disc space-y-1 pl-5 text-sm text-muted-foreground">
                {result.alternates.map((a) => (
                  <li key={a.slice(0, 40)}>{a}</li>
                ))}
              </ul>
            </div>
          )}
          {result.package && <CalcResults result={result.package} />}
        </div>
      )}
    </div>
  );
}
